import api from './api';
import axios from 'axios';
import { apiConfig } from './api';
import { createSessions } from '../models';
import { useAuthStore } from '../stores/authStore';
import { getSessionIdFromToken } from '../utils/jwt';

/**
 * Login with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {string} deviceName - Optional device name
 * @returns {Promise<{access_token: string, refresh_token: string}>}
 */
export const login = async (email, password, deviceName = '') => {
  try {
    const response = await api.post('/api/auth/login', {
      email,
      password,
      device_name: deviceName,
    });
    
    // Interceptor handles unified structure, return data directly
    return response.data;
  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
};

/**
 * Logout current session
 * @returns {Promise<void>}
 */
export const logout = async () => {
  try {
    await api.post('/api/auth/logout');
  } catch (error) {
    console.error('Logout failed:', error);
    throw error;
  }
};

/**
 * Fetch current user information
 * @returns {Promise<Object>} User data
 */
export const fetchCurrentUser = async () => {
  try {
    const response = await api.get('/api/auth/me');
    
    // Interceptor handles unified structure, return data directly
    return response.data;
  } catch (error) {
    console.error('Failed to fetch current user:', error);
    throw error;
  }
};

/**
 * Refresh authentication token
 * @param {string} refreshToken - The refresh token
 * @returns {Promise<{access_token: string, refresh_token: string}>}
 */
export const refreshAuthToken = async (refreshToken) => {
  try {
    const response = await axios.post(`${apiConfig.apiUrl}/api/auth/refresh-token`, {}, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${refreshToken}`,
      },
    });
    
    // Handle unified structure manually since using axios directly (not api instance)
    const apiResponse = response.data;
    if (apiResponse && typeof apiResponse === 'object' && 'success' in apiResponse) {
      if (!apiResponse.success) {
        throw new Error(apiResponse.error?.message || 'Token refresh failed');
      }
      return apiResponse.data;
    }
    
    return apiResponse;
  } catch (error) {
    console.error('Refresh token failed:', error);
    throw error;
  }
};

/**
 * Fetch all user sessions
 * @returns {Promise<Array<Session>>} Array of Session models
 */
export const fetchSessions = async () => {
  try {
    const response = await api.get('/api/auth/sessions');
    const { accessToken } = useAuthStore.getState();
    const currentSessionId = getSessionIdFromToken(accessToken);
    // Interceptor handles unified structure, response.data is already unwrapped
    const sessionsData = response.data.sessions || response.data || [];
    const sessionModels = createSessions(sessionsData, currentSessionId);
    return sessionModels;
  } catch (error) {
    console.error('Failed to fetch sessions:', error);
    throw error;
  }
};

/**
 * Revoke a specific session
 * @param {string} sessionId - The session ID to revoke
 * @returns {Promise<void>}
 */
export const revokeSession = async (sessionId) => {
  try {
    await api.delete(`/api/auth/sessions/${sessionId}`);
  } catch (error) {
    console.error('Failed to revoke session:', error);
    throw error;
  }
};

/**
 * Revoke all other sessions (except current)
 * @returns {Promise<void>}
 */
export const revokeAllOtherSessions = async () => {
  try {
    await api.delete('/api/auth/sessions/revoke-all-others');
  } catch (error) {
    console.error('Failed to revoke other sessions:', error);
    throw error;
  }
};

/**
 * Change user password
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise<{success: boolean}>}
 */
export const changePassword = async (currentPassword, newPassword) => {
  try {
    const response = await api.post('/api/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    
    // Check if response follows unified structure
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      if (!response.data.success) {
        throw new Error('Password change failed');
      }
      return response.data.data || { success: true };
    }
    
    return response.data;
  } catch (error) {
    console.error('Change password failed:', error);
    throw error;
  }
};

/**
 * Request password reset
 * @param {string} email - User email
 * @returns {Promise<{success: boolean}>}
 */
export const requestPasswordReset = async (email) => {
  try {
    const response = await api.post('/api/auth/password-reset/request', {
      email,
    });
    
    // Interceptor handles unified structure, return data directly
    return response.data || { success: true };
  } catch (error) {
    console.error('Request password reset failed:', error);
    throw error;
  }
};

/**
 * Reset password with token
 * @param {string} token - Password reset token
 * @param {string} newPassword - New password
 * @returns {Promise<{success: boolean}>}
 */
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await api.post('/api/auth/password-reset/reset', {
      token,
      new_password: newPassword,
    });
    
    // Interceptor handles unified structure, return data directly
    return response.data || { success: true };
  } catch (error) {
    console.error('Reset password failed:', error);
    throw error;
  }
};

/**
 * Request email change
 * @param {string} password - Current password
 * @param {string} newEmail - New email address
 * @returns {Promise<{success: boolean}>}
 */
export const requestEmailChange = async (password, newEmail) => {
  try {
    const response = await api.post('/api/auth/change-email/request', {
      password,
      new_email: newEmail,
    });
    
    // Interceptor handles unified structure, return data directly
    return response.data || { success: true };
  } catch (error) {
    console.error('Request email change failed:', error);
    throw error;
  }
};
